import { isAxiosNetworkError } from "src/utils/isAxiosNetworkError";

/**
 * Filter out browser extension errors from console and error handlers
 * Call this function once at app initialization.
 *
 * @param onConsoleError optional reporter invoked for every genuine (non
 *   extension) console.error — used to forward those errors to Sentry.
 */
export const isIntentionalRequestCancellation = (reason: unknown): boolean =>
	Boolean(
		reason &&
			typeof reason === "object" &&
			"config" in reason &&
			(reason as { config?: { pocketsflowIntentionalCancellation?: boolean } })
				.config?.pocketsflowIntentionalCancellation,
	);

export const filterExtensionErrors = (
	onConsoleError?: (args: unknown[]) => void,
) => {
	// Filter out browser extension errors from console.error
	const originalError = console.error;
	console.error = (...args) => {
		if (typeof args[0] === "string") {
			// Filter out chrome extension errors
			if (args[0].includes("chrome-extension://")) {
				return;
			}
			// Filter out firefox extension errors
			if (args[0].includes("moz-extension://")) {
				return;
			}
			// Filter out safari extension errors
			if (args[0].includes("safari-extension://")) {
				return;
			}
		}
		// Forward real errors to Sentry before logging to the console.
		onConsoleError?.(args);
		originalError.apply(console, args);
	};

	// Filter window errors from extensions
	window.addEventListener("error", (event) => {
		if (
			event.filename?.includes("chrome-extension://") ||
			event.filename?.includes("moz-extension://") ||
			event.filename?.includes("safari-extension://")
		) {
			event.preventDefault();
			return;
		}
	});

	// Filter unhandled promise rejections from extensions and Axios transport
	// failures (offline / abort). Those must not page as unhandled crashes
	// (POC-423 / FRONTEND-5). Partner 4xx/5xx keep `response` and still surface.
	window.addEventListener("unhandledrejection", (event) => {
		const message = event.reason?.message || event.reason?.toString() || "";
		const intentionalRequestCancellation = isIntentionalRequestCancellation(
			event.reason,
		);
		if (
			intentionalRequestCancellation ||
			message.includes("chrome-extension://") ||
			message.includes("moz-extension://") ||
			message.includes("safari-extension://") ||
			isAxiosNetworkError(event.reason)
		) {
			event.preventDefault();
			return;
		}
	});
};
